import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Trash2, Loader2, GripVertical, Package, 
  X, Check, Edit2, FolderPlus, ChevronDown, ChevronRight
} from "lucide-react";
import { getApiBaseUrl } from '@/config/api';

const API = getApiBaseUrl();

/**
 * Extract item/prop name from filename
 * "Shotgun_left.png" -> "Shotgun"
 * "shotgun point blank.png" -> "Shotgun"
 * "Key_gold_front.png" -> "Key"
 */
const extractItemName = (filename) => {
  // Remove extension
  let nameWithoutExt = filename.replace(/\.[^/.]+$/, "");
  
  // Split by common separators: space, underscore, dash
  const parts = nameWithoutExt.split(/[\s_-]+/);
  
  // Take the first meaningful part as item name
  let name = parts[0] || nameWithoutExt;
  
  // Skip common descriptors as first word
  const skipWords = ['left', 'right', 'front', 'back', 'side', 'top', 'bottom', 'angle', 'view', 'close', 'wide', 'point', 'blank'];
  if (skipWords.includes(name.toLowerCase()) && parts.length > 1) {
    name = parts[1];
  }
  
  // Capitalize first letter
  name = name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  
  return name;
};

/**
 * Group files by extracted item name
 */
const groupFilesByItem = (files) => {
  const groups = {};
  
  files.forEach((file, index) => {
    const name = extractItemName(file.name);
    if (!groups[name]) {
      groups[name] = [];
    }
    groups[name].push({
      id: `file-${index}-${Date.now()}`,
      file,
      preview: URL.createObjectURL(file),
      originalName: file.name
    });
  });
  
  return groups;
};

/**
 * Smart Import Modal for item/prop images
 * Groups uploaded files by detected item names and allows reorganization
 */
export default function SmartImportItemsModal({ 
  open, 
  onClose, 
  files,
  onConfirm,
  projectId
}) {
  // State for item groups: { itemName: [{ id, file, preview }] }
  const [groups, setGroups] = useState({});
  // Editing group name
  const [editingGroup, setEditingGroup] = useState(null);
  const [editName, setEditName] = useState("");
  // Drag state
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverGroup, setDragOverGroup] = useState(null);
  // Loading states
  const [importing, setImporting] = useState(false);
  // Collapsed groups
  const [collapsedGroups, setCollapsedGroups] = useState({});
  // Ref to prevent duplicate drops
  const isDropping = useRef(false);
  // Upload progress state
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, currentFile: '' });

  // Initialize groups when files change
  useEffect(() => {
    if (files && files.length > 0) {
      const initialGroups = groupFilesByItem(files);
      setGroups(initialGroups);
    }
  }, [files]);

  // Cleanup previews on unmount
  useEffect(() => {
    return () => {
      Object.values(groups).flat().forEach(item => {
        if (item.preview) URL.revokeObjectURL(item.preview);
      });
    };
  }, []);

  // Get sorted group names
  const sortedGroupNames = Object.keys(groups).sort((a, b) => 
    a.toLowerCase().localeCompare(b.toLowerCase())
  );

  // Handle drag start
  const handleDragStart = (e, item, sourceGroup) => {
    setDraggedItem({ item, sourceGroup });
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", item.id);
  };

  // Handle drag over
  const handleDragOver = (e, groupName) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    setDragOverGroup(groupName);
  };

  // Handle drag leave
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverGroup(null);
  };

  // Handle drop
  const handleDrop = (e, targetGroup) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverGroup(null);
    
    // Prevent duplicate drops using ref
    if (isDropping.current) {
      return;
    }
    
    if (!draggedItem || draggedItem.sourceGroup === targetGroup) {
      setDraggedItem(null);
      return;
    }
    
    // Lock to prevent duplicate processing
    isDropping.current = true;
    
    const { item, sourceGroup } = draggedItem;
    
    // Clear draggedItem immediately
    setDraggedItem(null);
    
    setGroups(prev => {
      const newGroups = { ...prev };
      
      // Check if item already exists in target (prevent duplicates)
      const alreadyInTarget = newGroups[targetGroup]?.some(i => i.id === item.id);
      if (alreadyInTarget) {
        return prev;
      }
      
      // Check if source group still has the item (might have been moved already)
      const itemInSource = newGroups[sourceGroup]?.some(i => i.id === item.id);
      if (!itemInSource) {
        return prev;
      }
      
      // Remove from source
      newGroups[sourceGroup] = newGroups[sourceGroup].filter(i => i.id !== item.id);
      
      // Remove empty group
      if (newGroups[sourceGroup].length === 0) {
        delete newGroups[sourceGroup];
      }
      
      // Add to target
      if (!newGroups[targetGroup]) {
        newGroups[targetGroup] = [];
      }
      newGroups[targetGroup].push(item);
      
      return newGroups;
    });
    
    // Unlock after a short delay
    setTimeout(() => {
      isDropping.current = false;
    }, 100);
  };

  // Handle creating new item group
  const handleCreateNewGroup = () => {
    const baseName = "New Item";
    let name = baseName;
    let counter = 1;
    
    while (groups[name]) {
      name = `${baseName} ${counter}`;
      counter++;
    }
    
    setGroups(prev => ({
      ...prev,
      [name]: []
    }));
    
    // Start editing the name
    setEditingGroup(name);
    setEditName(name);
  };

  // Handle renaming group
  const handleRenameGroup = (oldName) => {
    if (!editName.trim() || editName === oldName) {
      setEditingGroup(null);
      return;
    }
    
    // Check if name already exists
    if (groups[editName] && editName !== oldName) {
      // Merge into existing group
      setGroups(prev => {
        const newGroups = { ...prev };
        newGroups[editName] = [...(newGroups[editName] || []), ...newGroups[oldName]];
        delete newGroups[oldName];
        return newGroups;
      });
    } else {
      // Rename
      setGroups(prev => {
        const newGroups = {};
        Object.entries(prev).forEach(([key, value]) => {
          if (key === oldName) {
            newGroups[editName] = value;
          } else {
            newGroups[key] = value;
          }
        });
        return newGroups;
      });
    }
    
    setEditingGroup(null);
    setEditName("");
  };

  // Handle deleting an image
  const handleDeleteImage = (groupName, itemId) => {
    setGroups(prev => {
      const newGroups = { ...prev };
      newGroups[groupName] = newGroups[groupName].filter(i => i.id !== itemId);
      
      if (newGroups[groupName].length === 0) {
        delete newGroups[groupName];
      }
      
      return newGroups;
    });
  };

  // Handle deleting entire group
  const handleDeleteGroup = (groupName) => {
    setGroups(prev => {
      const newGroups = { ...prev };
      // Revoke URLs
      newGroups[groupName]?.forEach(item => {
        if (item.preview) URL.revokeObjectURL(item.preview);
      });
      delete newGroups[groupName];
      return newGroups;
    });
  };

  // Toggle group collapse
  const toggleGroupCollapse = (groupName) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  // Handle confirm import
  const handleConfirm = async () => {
    setImporting(true);
    const totalFiles = Object.values(groups).reduce((sum, g) => sum + g.length, 0);
    setUploadProgress({ current: 0, total: totalFiles, currentFile: '' });
    
    try {
      await onConfirm(groups, (current, total, filename) => {
        setUploadProgress({ current, total, currentFile: filename });
      });
      onClose();
    } catch (error) {
      console.error("Import failed:", error);
    } finally {
      setImporting(false);
      setUploadProgress({ current: 0, total: 0, currentFile: '' });
    }
  };

  // Count total files
  const totalFiles = Object.values(groups).reduce((sum, g) => sum + g.length, 0);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col bg-zinc-900 border-zinc-700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Package className="w-5 h-5 text-orange-400" />
            Smart Import - Items/Props
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Images have been grouped by detected item name. Drag images between groups to reorganize.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2" style={{ maxHeight: '55vh' }}>
          <div className="space-y-4">
            {sortedGroupNames.length === 0 ? (
              <div className="text-center py-12 text-zinc-500">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No images to import</p>
              </div>
            ) : (
              sortedGroupNames.map(groupName => {
                const groupItems = groups[groupName];
                const isCollapsed = collapsedGroups[groupName];
                const isDragOver = dragOverGroup === groupName;
                
                return (
                  <div
                    key={groupName}
                    className={`border rounded-lg overflow-hidden transition-colors ${
                      isDragOver 
                        ? 'border-orange-500 bg-orange-500/10' 
                        : 'border-zinc-700 bg-zinc-800/50'
                    }`}
                    onDragOver={(e) => handleDragOver(e, groupName)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, groupName)}
                  >
                    {/* Group Header */}
                    <div className="flex items-center gap-2 p-3 bg-zinc-800 border-b border-zinc-700">
                      <button
                        onClick={() => toggleGroupCollapse(groupName)}
                        className="text-zinc-400 hover:text-white"
                      >
                        {isCollapsed ? (
                          <ChevronRight className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                      
                      <Package className="w-4 h-4 text-orange-400" />
                      
                      {editingGroup === groupName ? (
                        <div className="flex items-center gap-2 flex-1">
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleRenameGroup(groupName);
                              if (e.key === 'Escape') setEditingGroup(null);
                            }}
                            className="h-7 text-sm bg-zinc-900 border-zinc-600"
                            autoFocus
                          />
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleRenameGroup(groupName)}
                            className="w-7 h-7 text-green-400"
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => setEditingGroup(null)}
                            className="w-7 h-7 text-zinc-400"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <span className="font-medium text-white flex-1">{groupName}</span>
                          <Badge variant="secondary" className="bg-zinc-700 text-zinc-300">
                            {groupItems.length} image{groupItems.length !== 1 ? 's' : ''}
                          </Badge>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => {
                              setEditingGroup(groupName);
                              setEditName(groupName);
                            }}
                            className="w-7 h-7 text-zinc-400 hover:text-white"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleDeleteGroup(groupName)}
                            className="w-7 h-7 text-zinc-400 hover:text-red-400"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                    </div>

                    {/* Group Images */}
                    {!isCollapsed && (
                      <div className="p-3">
                        <div className="grid grid-cols-5 gap-2">
                          {groupItems.map((item) => (
                            <div
                              key={item.id}
                              draggable
                              onDragStart={(e) => handleDragStart(e, item, groupName)}
                              className={`relative group aspect-square rounded-md overflow-hidden border cursor-move transition-all ${
                                draggedItem?.item.id === item.id
                                  ? 'opacity-50 border-orange-500'
                                  : 'border-zinc-600 hover:border-zinc-400'
                              }`}
                            >
                              <img
                                src={item.preview}
                                alt={item.originalName}
                                className="w-full h-full object-contain bg-zinc-900"
                                draggable={false}
                              />
                              
                              {/* Drag handle */}
                              <div className="absolute top-1 left-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <div className="bg-black/70 rounded p-0.5">
                                  <GripVertical className="w-3 h-3 text-white" />
                                </div>
                              </div>
                              
                              {/* Delete button */}
                              <button
                                onClick={() => handleDeleteImage(groupName, item.id)}
                                className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity bg-red-500/80 rounded p-0.5 hover:bg-red-500"
                              >
                                <X className="w-3 h-3 text-white" />
                              </button>
                              
                              {/* Filename */}
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1">
                                <p className="text-[9px] text-white truncate">{item.originalName}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}

            {/* Add New Group Button */}
            <Button
              variant="outline"
              onClick={handleCreateNewGroup}
              className="w-full border-dashed border-zinc-600 text-zinc-400 hover:text-white hover:border-zinc-400"
            >
              <FolderPlus className="w-4 h-4 mr-2" />
              Add New Item Group
            </Button>
          </div>
        </div>

        <DialogFooter className="border-t border-zinc-700 pt-4">
          <div className="flex flex-col w-full gap-3">
            {/* Upload Progress */}
            {importing && uploadProgress.total > 0 && (
              <div className="bg-zinc-800 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-zinc-300">
                    Uploading {uploadProgress.current} of {uploadProgress.total}
                  </span>
                  <span className="text-sm text-orange-400">
                    {Math.round((uploadProgress.current / uploadProgress.total) * 100)}%
                  </span>
                </div>
                <div className="w-full h-2 bg-zinc-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-orange-500 transition-all duration-300"
                    style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                  />
                </div>
                {uploadProgress.currentFile && (
                  <p className="text-xs text-zinc-500 mt-1 truncate">
                    {uploadProgress.currentFile}
                  </p>
                )}
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <p className="text-sm text-zinc-400">
                {sortedGroupNames.length} item{sortedGroupNames.length !== 1 ? 's' : ''} • {totalFiles} image{totalFiles !== 1 ? 's' : ''}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose} disabled={importing} className="border-zinc-600">
                  Cancel
                </Button>
                <Button 
                  onClick={handleConfirm}
                  disabled={importing || totalFiles === 0}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  {importing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Import {totalFiles} Image{totalFiles !== 1 ? 's' : ''}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
